import {CdkTableDataSourceInput} from '@angular/cdk/table';
import {CommonModule, NgTemplateOutlet} from '@angular/common';
import {
    AfterContentInit,
    Component,
    ContentChild,
    ContentChildren,
    DestroyRef,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormBuilder, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {MatIconButton} from '@angular/material/button';
import {MatCheckbox} from '@angular/material/checkbox';
import {MatIcon} from '@angular/material/icon';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {PageEvent} from '@angular/material/paginator';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import {Observable, Subscription} from 'rxjs';
import {ITableColumn} from '../../../core/common/interfaces/table-column.interface';
import {DataTableService} from '../../../core/services/data-table.service';
import {TableActionDirective} from './table-action.directive';
import {TableCellDirective} from './table-cell.directive';
import {NgxDatatableModule} from '@swimlane/ngx-datatable';
import {MatSelectModule} from '@angular/material/select';
import {MatSort, MatSortModule, Sort} from '@angular/material/sort';
import {CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray} from '@angular/cdk/drag-drop';
import {WidthResizableDirective} from '../../../core/directives/width-resizable.directive';
import {PasFiltersStoreService} from "../../../core/services/pas-store.service";
import {PasFilterType} from "../side-bar-double/menu-data.service";
import {PeriodColumnsService} from "../../../modules/routing/report-occupied-places/period-columns.service";
import {CdkConnectedOverlay, CdkOverlayOrigin} from "@angular/cdk/overlay";


interface ICellTemplates {
    [key: string]: TemplateRef<unknown>;
}

export const SETTINGS_COLUMN = 'settings';
export const EXTENDED_SETTINGS_COLUMN = 'extendedSettings';

type SelectableTableColumn = ITableColumn & { isSelected?: boolean, list?: string[] };

@Component({
    selector: 'ostso-table',
    standalone: true,
    imports: [
        MatTable,
        MatHeaderCell,
        MatCell,
        MatHeaderRow,
        MatRow,
        MatColumnDef,
        MatHeaderRowDef,
        MatRowDef,
        MatCellDef,
        MatHeaderCellDef,
        NgTemplateOutlet,
        MatIconButton,
        MatMenuItem,
        MatMenu,
        MatMenuTrigger,
        MatCheckbox,
        MatIcon,
        MatSortModule,
        FormsModule,
        ReactiveFormsModule,
        NgxDatatableModule,
        MatSelectModule,
        CommonModule,
        CdkDropList,
        CdkDrag,
        WidthResizableDirective,
        CdkConnectedOverlay,
        CdkOverlayOrigin,
    ],
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
})
export class TableComponent<T> implements OnInit, AfterContentInit {

    @Input() controls: boolean = true;
    @Input() filterValues?: any;
    @Input({
        required: true
    }) updateList?: Observable<void>;
    @Input() lastIsSortable?: boolean;

    @Input() set type(x: PasFilterType) {
        if (x) {
            this.dataFromType = this.filterStore.get(x)
        }
    }

    @ViewChild(MatTable, {read: ElementRef}) tableRef!: ElementRef<HTMLTableElement>;

    @Output() sortedColumn: EventEmitter<Sort> = new EventEmitter<Sort>();
    @Output() pageChange: EventEmitter<PageEvent> = new EventEmitter<PageEvent>;
    @Output() rowSelected = new EventEmitter<T>;
    @Output() listSelected = new EventEmitter<string[]>;
    @ContentChildren(TableCellDirective) cellDirectives: QueryList<TableCellDirective> | undefined;
    @ContentChild(TableActionDirective) actionMenuDirective?: TableActionDirective;
    @ContentChildren(MatMenuTrigger) menuTriggers!: MatMenuTrigger[];

    @ViewChild(MatSort) matSort!: MatSort;


    data: CdkTableDataSourceInput<T> = [];

    columns: SelectableTableColumn[] = [];

    displayedColumns: string[] = [];

    cellTemplates: ICellTemplates = {};
    actionTemplate?: TemplateRef<unknown>;

    recordCount = 25;
    pageSize = 10;

    page = 1;


    protected readonly SETTINGS_COLUMN = SETTINGS_COLUMN;
    protected readonly EXTENDED_SETTINGS_COLUMN = EXTENDED_SETTINGS_COLUMN;

    private subscription = new Subscription();

    form = this.fb.group({
        pageSizeControl: 10
    });


    dataFromType?: any = undefined



    constructor(
        private dataTableService: DataTableService,
        private destroyRef: DestroyRef,
        private fb: FormBuilder,
        private filterStore: PasFiltersStoreService,
        public periodService: PeriodColumnsService
    )
    {
    }

    selectedPeriodSignal = this.periodService.getSelectedSignal()
    protected isOpen: boolean = false;

    ngOnInit(): void {
        console.log('MatSort', this.matSort);

        this.setColumns();
        this.initDataObserver();

        this.subscription.add(this.form.get('pageSizeControl')!.valueChanges.subscribe(value => {
            this.page = 1;
            this.pageSize = value!;

            const pageData: PageEvent = {
                pageIndex: 1,
                pageSize: value!,
                length: this.recordCount
            };
            this.onPageChange(pageData);

        }));

    }

    ngAfterContentInit(): void {
        this.initTemplates();

        setTimeout(() => {
            this.matSort.sortChange.subscribe(value => {
                console.log('sortChange', value);
                if (value.active === EXTENDED_SETTINGS_COLUMN) return
                this.dataTableService.setSorting(value);
            })
        });

        setTimeout(() => {
            const sorting: Sort | null = this.dataTableService.getSorting();
            sorting && this.matSort.sort({id: sorting.active, start: sorting.direction, disableClear: false});
        })

    }

    initDataObserver(): void {
        this.dataTableService.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
            if (data) {
                this.data = data.values;
                this.recordCount = data.pager?.countTotal;
            }
        });
    }


    setColumns(): void {
        this.columns = this.dataTableService.getColumns();

        if (this.dataTableService.showCustomColumns) {
            this.columns.push(
                {
                    name: SETTINGS_COLUMN,
                    sticky: true,
                    isRequired: true
                });

        }
        this.updateSelectedColumns();
    }

    changedSelectedColumn(): void {
        this.dataTableService.setCustomColumns(
            this.columns
                .filter(value => value.isSelected)
                .map(value => value.name)
        );

        this.updateSelectedColumns();
    }

    updateSelectedColumns(): void {
        const selectedColumns = this.dataTableService.getCustomColumns();

        this.columns.forEach(value => {
            value.isSelected = value.isRequired || value.default || selectedColumns.includes(value.name);
            value.default = false;
        });
        this.displayedColumns = this.columns
            .filter(value => value.isSelected)
            .map(value => value.name);

        this.displayedColumns = selectedColumns.map(value => this.displayedColumns.includes(value) ? value : null)
            .filter((value): value is string => value !== null);
    }

    setDirectlySelectedColumns(): void {
        this.columns = this.dataTableService.getColumns();
        const selectedColumns = this.dataTableService.getCustomColumns();
        console.log(this.columns, this.data)
        this.displayedColumns = selectedColumns
    }

    initTemplates(): void {
        if (this.cellDirectives && this.cellDirectives.length) {
            this.cellTemplates = this.cellDirectives.reduce((acc: ICellTemplates, cur) => {
                acc[cur.name] = cur.template;
                return acc;
            }, {});
        }

        if (this.actionMenuDirective) {
            this.actionTemplate = this.actionMenuDirective.template;
        }
    }

    onPageChange(page: PageEvent): void {
        this.pageChange.emit(page);
    }

    setPage(page: any): void {
        page.page && (this.page = page.page);
        page.pageSize && (this.pageSize = page.pageSize)
        const pageData: PageEvent = {
            pageIndex: page.page ?? this.page,
            pageSize: page.pageSize ?? this.pageSize,
            length: page.recordCount ?? this.recordCount
        };
        this.onPageChange(pageData);
    }

    setExternalPage(page: PageEvent):void {
        page.pageIndex && (this.page = page.pageIndex)
        page.pageSize && (this.pageSize = page.pageSize)
        page.length && (this.recordCount = page.length)
        this.form.get('pageSizeControl')!.setValue(+this.pageSize, {emitEvent: false})
    }

    sortChange($event: Sort): void {
        if ($event.active === EXTENDED_SETTINGS_COLUMN) {
            return
        }
        this.sortedColumn.emit($event);
    }

    hasArchived(element: any): boolean {
        const res = element?.status?.code === 'Archive';
        return res;
    }

    drop(event: CdkDragDrop<any, any>): void {
        moveItemInArray(this.displayedColumns, event.previousIndex, event.currentIndex);
        this.dataTableService.setCustomColumns(this.displayedColumns);
    }

}

